import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'unistore/react'
import i18n from 'i18next'
import * as dateFns from 'date-fns'
import scrollToElement from 'scroll-to-element'
import * as actions from '../../helpers/actions.jsx'

// Components
import InstantQuoteForm from './InstantQuoteForm.jsx'
import InstantQuoteResult from './InstantQuoteResult.jsx'
// Services
import * as domBridge from '../../Services/domBridge.jsx'
import * as storage from '../../Services/storage.jsx' //tofix
import {
  quoteKinds,
  getQuote,
  getRates,
  getTranslatorsCountForLanguages,
} from '../../Services/api.jsx'
import * as Gtm from '../../Services/gtm.jsx'
import {
  getQuoteCode,
  getQuoteCodeFromUrl,
  dateFnsLocalizedFormat,
  dateFnsLocalizedTimeFormat,
  convertTimeToTimezone,
  isMostPopularLanguage,
  apiErrorToMessage,
  sortByOptionName,
  timeZoneToLabel,
  decodeJwt,
  getMarketingQuoteKindByVariant,
} from '../../Services/helpers.jsx'
import * as hubspot from '../../Services/hubspot_api.jsx'
import {features} from '../../Services/features.jsx'
import {experimentTracker} from '../../Services/tracker.jsx'
import {featureFlags} from '../../Services/featureFlags.jsx'
import {
  createInitialFormState,
  createInitialQuotesState,
  createInitialUIState,
  createInitialConfigState,
  formReducer,
  quotesReducer,
  uiReducer,
  configReducer,
} from './instantQuoteReducers.jsx'
import {useInstantQuoteInitialization} from './useInstantQuoteInitialization.jsx'

function InstantQuote({wordCount, files, setWordCount, removeFiles}) {
  // Hide the static HTML form immediately when React starts rendering
  // This prevents showing both forms at the same time
  React.useEffect(() => {
    domBridge.hideHtmlStaticInstantQuoteForm()
  }, [])

  const forceUseDefaultValues =
      !domBridge.shouldInstantQuoteUseLocalStorageParams()
  const instantQuotePreselectedTargetLanguages =
      domBridge.getAndUpdateStoredInstantQuotePreselectedTargetLanguages(
          forceUseDefaultValues,
      ) || []
  const languages = domBridge.getInstantQuoteLanguages().sort(sortByOptionName)

  // Static data that doesn't change
  const [languagesState] = useState(languages)
  const [mostPopularTargetLanguages] = useState(
      languages.filter(isMostPopularLanguage),
  )
  const [subjects] = useState(
      domBridge.getInstantQuoteSubjects().sort(sortByOptionName),
  )

  // Initial state values
  const initialFormState = createInitialFormState(
      forceUseDefaultValues,
      instantQuotePreselectedTargetLanguages,
  )
  const initialQuotesState = createInitialQuotesState()
  const initialUIState = createInitialUIState()
  const initialConfigState = createInitialConfigState()

  // Use reducers
  const [formState, dispatchForm] = useReducer(formReducer, initialFormState)
  const [quotesState, dispatchQuotes] = useReducer(
      quotesReducer,
      initialQuotesState,
  )
  const [uiState, dispatchUI] = useReducer(uiReducer, initialUIState)
  const [configState, dispatchConfig] = useReducer(
      configReducer,
      initialConfigState,
  )

  // Destructure for easier access
  const {
    sourceLanguage,
    targetLanguages,
    subject,
    deliveryDate,
    deliveryMaxSelectableDate,
  } = formState

  const {isFormSubmitted, isFormChanged} = uiState

  const {
    posteditingQuote,
    translationQuote,
    translationRevisionQuote,
    transcreationQuote,
    laraInstantQuote,
    laraHrQuote,
    posteditingQuoteIsPending,
    translationQuoteIsPending,
    translationRevisionQuoteIsPending,
    transcreationQuoteIsPending,
    laraInstantQuoteIsPending,
  } = quotesState

  const {showQuotes, isDeliveryDateModalVisible, translatorsCount} = uiState

  const {
    currency,
    rates,
    selectedTimeZone,
    firstCardSolution,
    secondCardSolution,
    thirdCardSolution,
  } = configState

  // Define onChangeCurrency before useEffect so it can be referenced
  const onChangeCurrency = useCallback(async (eventOrValue) => {
    const currencyValue = eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue
    const newRates = await getRates()
    dispatchConfig({type: 'SET_CURRENCY', payload: currencyValue})
    dispatchConfig({type: 'SET_RATES', payload: newRates})
  }, [])

  // Define isFormValid and getQuotes before handleSubmit
  const isFormValid = useCallback(() => {
    return (
        sourceLanguage &&
        sourceLanguage.id &&
        targetLanguages.length > 0 &&
        subject &&
        subject.id &&
        wordCount &&
        !isNaN(wordCount)
    )
  }, [sourceLanguage, targetLanguages, subject, wordCount])

  const storeInstantQuoteFormValues = useCallback(() => {
    sourceLanguage && storage.storeInstantQuoteSourceLanguage(sourceLanguage)
    targetLanguages && storage.storeInstantQuoteTargetLanguages(targetLanguages)
    subject && storage.storeInstantQuoteSubject(subject)
    wordCount && storage.storeInstantQuoteWords(wordCount)
  }, [sourceLanguage, targetLanguages, subject, wordCount])

  const hb_storeInstantQuotesFormValues = useCallback(
      async (quoteCode, prices) => {
        if (
            sourceLanguage &&
            targetLanguages &&
            subject &&
            wordCount &&
            deliveryDate
        ) {
          const hb_instantQuote = {
            code: quoteCode,
            sourceLanguage,
            targetLanguages,
            subject,
            wordCount,
            deliveryDate,
            ...prices,
          }

          // if user is not logged in, then store quote in localStorage: will be sent to Hubspot whenever login or signup occur
          //if (!window.universal_variable.user) {
          const userToken = storage.getCurrentUserToken()
          if (!userToken) {
            storage.hbapi_storeInstantQuote(quoteCode, {
              ...hb_instantQuote,
              timestamp: new Date().toISOString(),
            })
            storage.hb_storeInstantQuote(quoteCode, {
              ...hb_instantQuote,
              timestamp: new Date().getTime(),
            })
          }
          // if user is logged in, then track on hubspot immediately using the API
          else {
            const user = decodeJwt(userToken)
            await hubspot.hbapi_trackQuote(user.email, quoteCode, hb_instantQuote)
          }
        }
      },
      [sourceLanguage, targetLanguages, subject, wordCount, deliveryDate],
  )

  const getQuotes = useCallback(
      async (deliveryDateOverride) => {
        const effectiveDeliveryDate = deliveryDateOverride || deliveryDate
        const sourceLanguageId = sourceLanguage ? sourceLanguage.id : ''
        const deliveryDateId =
            effectiveDeliveryDate.date && effectiveDeliveryDate.timeZone
                ? convertTimeToTimezone(
                    effectiveDeliveryDate.date,
                    effectiveDeliveryDate.timeZone,
                ).toISOString()
                : undefined

        const dateFromSystem = !deliveryDateId
        const lastDeliveryMaxSelectableDate = deliveryMaxSelectableDate

        const targetLanguageIds = targetLanguages.map((language) => language.id)
        const quoteArguments = {
          source: sourceLanguageId,
          target: targetLanguageIds,
          words: wordCount,
          subject: subject.id || '',
          when: deliveryDateId,
        }

        storeInstantQuoteFormValues() //tocheck: what's going on here? no arguments?

        if (sourceLanguage && targetLanguages) {
          try {
            const translatorsCountValue = await getTranslatorsCountForLanguages(
                sourceLanguage,
                targetLanguages,
            )
            dispatchUI({
              type: 'SET_TRANSLATORS_COUNT',
              payload: translatorsCountValue,
            })
          } catch (error) {
            console.log('Error getting translators count:', error)
            // Continue execution even if translators count fails
          }
        }

        try {
          const quotes = {
            posteditingQuote: null,
            translationQuote: null,
            translationRevisionQuote: null,
            transcreationQuote: null,
            laraInstantQuote: null,
            laraHrQuote: null,
          }
          const quotesToDo = await features.getQuotesToDo()
          if (quotesToDo.includes(quoteKinds.postediting)) {
            quotes.posteditingQuote = getQuote({
              ...quoteArguments,
              kind: quoteKinds.postediting,
            })
          }
          if (quotesToDo.includes(quoteKinds.translation)) {
            quotes.translationQuote = getQuote({
              ...quoteArguments,
              kind: quoteKinds.translation,
            })
          }

          if (quotesToDo.includes(quoteKinds.translationRevision)) {
            quotes.translationRevisionQuote = getQuote({
              ...quoteArguments,
              kind: quoteKinds.translationRevision,
            })
          }

          if (quotesToDo.includes(quoteKinds.transcreation)) {
            quotes.transcreationQuote = getQuote({
              ...quoteArguments,
              kind: quoteKinds.transcreation,
            })
          }

          if (quotesToDo.includes(quoteKinds.lara)) {
            quotes.laraInstantQuote = getQuote({
              ...quoteArguments,
              kind: quoteKinds.lara,
            })
          }

          if (quotesToDo.includes(quoteKinds.laraHR)) {
            quotes.laraHrQuote = getQuote({
              ...quoteArguments,
              kind: quoteKinds.laraHR,
            })
          }

          const quoteKeys = Object.keys(quotes)
          const quoteValues = await Promise.all(Object.values(quotes))
          const resolvedQuotes = quoteKeys.reduce((acc, key, index) => {
            acc[key] = quoteValues[index]
            return acc
          }, {})

          const {
            posteditingQuote: posteditingQuoteResult,
            translationQuote: translationQuoteResult,
            translationRevisionQuote: translationRevisionQuoteResult,
            transcreationQuote: transcreationQuoteResult,
            laraInstantQuote: laraInstantQuoteResult,
            laraHrQuote: laraHrQuoteResult,
          } = resolvedQuotes

          const quotesArray = Object.values(resolvedQuotes).filter((q) => !!q)

          const quoteCodeValue = getQuoteCode(Object.values(quotesArray))
          hb_storeInstantQuotesFormValues(quoteCodeValue, {
            premiumPrice: translationRevisionQuoteResult?.price ?? 0,
            professionalPrice: translationQuoteResult?.price ?? 0,
            economyPrice: posteditingQuoteResult?.price ?? 0,
            creativePrice: transcreationQuoteResult?.price ?? 0,
            laraInstantPrice: laraInstantQuoteResult?.price ?? 0,
            laraHrPrice: laraHrQuoteResult?.price ?? 0,
          })
          window.history.pushState({}, document.title, window.location.pathname)
          window.location.hash = `quote-code=${quoteCodeValue}`

          const newDeliveryMaxSelectableDate = quotesArray.reduce(
              (latestDate, quote) => {
                if (!latestDate && quote && quote.deliveryDate && dateFromSystem)
                  return quote.deliveryDate
                else if (
                    latestDate &&
                    quote &&
                    quote.deliveryDate &&
                    dateFromSystem
                )
                  return dateFns.isAfter(quote.deliveryDate, latestDate)
                      ? quote.deliveryDate
                      : latestDate
                else if (lastDeliveryMaxSelectableDate)
                  return lastDeliveryMaxSelectableDate
                else return undefined
              },
              undefined,
          )

          dispatchUI({type: 'SET_SHOW_QUOTES', payload: true})
          dispatchQuotes({
            type: 'SET_QUOTES',
            payload: {
              posteditingQuote: posteditingQuoteResult,
              translationQuote: translationQuoteResult,
              translationRevisionQuote: translationRevisionQuoteResult,
              transcreationQuote: transcreationQuoteResult,
              laraInstantQuote: laraInstantQuoteResult,
              laraHrQuote: laraHrQuoteResult,
            },
          })
          dispatchUI({type: 'SET_FORM_CHANGED', payload: false})
          dispatchUI({type: 'SET_TRANSLATORS_COUNT', payload: undefined})
          dispatchForm({
            type: 'SET_DELIVERY_MAX_DATE',
            payload: newDeliveryMaxSelectableDate,
          })
          const aiQuoteVariant = await featureFlags.getVariantAsync(
              'lara-redirect',
          )

          Gtm.setFunnelStep(2, {
            quoteType: getMarketingQuoteKindByVariant(aiQuoteVariant),
          })
          experimentTracker.showPrices()
        } catch (error) {
          domBridge.showSnackbar(apiErrorToMessage(error), 'error')

          dispatchUI({type: 'SET_SHOW_QUOTES', payload: true})
          dispatchQuotes({type: 'SET_ALL_QUOTES_LOADED'})
        }
      },
      [
        sourceLanguage,
        targetLanguages,
        deliveryMaxSelectableDate,
        subject,
        deliveryDate,
        wordCount,
        storeInstantQuoteFormValues,
        hb_storeInstantQuotesFormValues,
      ],
  )

  const handleSubmit = useCallback(
      async (event, {deliveryDateOverride} = {}) => {
        if (event) event.preventDefault()

        dispatchUI({type: 'SET_FORM_SUBMITTED', payload: true})
        dispatchUI({type: 'SET_FORM_CHANGED', payload: false})

        if (isFormValid()) {
          dispatchUI({type: 'SET_SHOW_QUOTES', payload: true})
          dispatchQuotes({type: 'SET_ALL_QUOTES_PENDING'})
          dispatchQuotes({type: 'RESET_QUOTES'})

          // Scroll after state updates (matching original setState callback behavior)
          setTimeout(() => {
            scrollToElement('#instant-quote-form__quotes', {
              offset: -32,
              ease: 'in-out-sine',
              duration: 800,
            })
          }, 0)

          getQuotes(deliveryDateOverride)
        } else {
          domBridge.scrollToFirstInvalidInput()
        }
      },
      [isFormValid, getQuotes],
  )

  const {initializeComponent, getMountedRef} = useInstantQuoteInitialization({
    languagesState,
    subjects,
    sourceLanguage,
    targetLanguages,
    subject,
    currency,
    selectedTimeZone,
    dispatchForm,
    dispatchConfig,
    onChangeCurrency,
    handleSubmit,
    setWordCount,
    removeFiles,
    files,
  })

  useEffect(() => {
    const mountedRef = getMountedRef()

    const runInitialization = async () => {
      await initializeComponent(mountedRef)
    }

    runInitialization()

    return () => {
      mountedRef.set(false)
      domBridge.currencyChangeRemoveEventListener(onChangeCurrency)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const getDeliveryDateText = useCallback(() => {
    if (deliveryDate.date) {
      const date = deliveryDate.date
      const labelTimezone = timeZoneToLabel(selectedTimeZone)
      return `${dateFnsLocalizedFormat(
          date,
          'eee dd MMM',
      )} ${dateFnsLocalizedTimeFormat(date)} ${labelTimezone}`
    } else {
      return i18n.t('Components.DeliveryDateModal.auto')
    }
  }, [deliveryDate, selectedTimeZone])

  const showDeliveryDateModal = useCallback(() => {
    dispatchUI({type: 'SET_DELIVERY_DATE_MODAL_VISIBLE', payload: true})
    setTimeout(() => {
      domBridge.addPreventScrollUnderModal()
      domBridge.mobileResizeUpdateModalContentHeight()
    }, 0)
  }, [])

  const hideDeliveryDateModal = useCallback(() => {
    dispatchUI({type: 'SET_DELIVERY_DATE_MODAL_VISIBLE', payload: false})
    domBridge.removePreventScrollUnderModal()
  }, [])

  const onDismissDeliveryDateModal = useCallback(
      () => hideDeliveryDateModal(),
      [hideDeliveryDateModal],
  )

  const handleSetSourceLanguage = useCallback((newSourceLanguage) => {
    dispatchForm({type: 'SET_SOURCE_LANGUAGE', payload: newSourceLanguage})
    dispatchUI({type: 'SET_FORM_CHANGED', payload: true})
  }, [])

  const toggleTargetLanguage = useCallback(
      (targetLanguage) => {
        const targetLanguagesFiltered = targetLanguages.filter(
            (language) => language.id !== targetLanguage.id,
        )
        const newTargetLanguages =
            targetLanguagesFiltered.length === targetLanguages.length
                ? targetLanguages.concat(targetLanguage)
                : targetLanguagesFiltered
        dispatchForm({type: 'SET_TARGET_LANGUAGES', payload: newTargetLanguages})
        dispatchUI({type: 'SET_FORM_CHANGED', payload: true})
      },
      [targetLanguages],
  )

  const resetSelectedTargetOptions = useCallback(() => {
    dispatchForm({type: 'SET_TARGET_LANGUAGES', payload: []})
    dispatchUI({type: 'SET_FORM_CHANGED', payload: true})
  }, [])

  const handleSetTargetLanguages = useCallback((newTargetLanguages) => {
    const targetLanguagesArray = Array.isArray(newTargetLanguages)
        ? newTargetLanguages
        : [newTargetLanguages]
    dispatchForm({type: 'SET_TARGET_LANGUAGES', payload: targetLanguagesArray})
    dispatchUI({type: 'SET_FORM_CHANGED', payload: true})
  }, [])

  const handleSetSubject = useCallback((newSubject) => {
    dispatchForm({type: 'SET_SUBJECT', payload: newSubject})
    dispatchUI({type: 'SET_FORM_CHANGED', payload: true})
  }, [])

  const setDeliveryDateAndTimezone = useCallback(
      (newDeliveryDate) => {
        dispatchForm({type: 'SET_DELIVERY_DATE', payload: newDeliveryDate})
        if (newDeliveryDate.timeZone) {
          dispatchConfig({
            type: 'SET_TIMEZONE',
            payload: newDeliveryDate.timeZone,
          })
          storage.sessionStoreCurrentTimezone(newDeliveryDate.timeZone)
        }
        dispatchUI({type: 'SET_FORM_CHANGED', payload: true})

        if (showQuotes) {
          // Pass the new delivery date directly to avoid stale closure issue
          // (React state update from dispatch above may not be flushed yet)
          setTimeout(() => {
            handleSubmit(null, {deliveryDateOverride: newDeliveryDate})
          }, 0)
        }
      },
      [showQuotes, handleSubmit],
  )

  const goToProjectDetails = useCallback(() => domBridge.navigateToOrder(), [])

  const onSelectQuote = useCallback(
      async (quote) => {
        const quoteCode = getQuoteCodeFromUrl()

        if (sourceLanguage && targetLanguages && subject) {
          // Check if human revision is enabled in storage and we're selecting a Lara quote
          const projectInfo = storage.getProjectInfo()
          const humanRevisionEnabled = projectInfo?.humanRevisionEnabled || false
          const isLaraQuote = quote.quoteKind === quoteKinds.lara
          const isLaraHRQuote = quote.quoteKind === quoteKinds.laraHR

          // If selecting a non-Lara quote, reset humanRevisionEnabled in storage
          if (!isLaraQuote && !isLaraHRQuote && humanRevisionEnabled) {
            storage.storeProjectInfo({
              ...projectInfo,
              humanRevisionEnabled: false,
            })
          }

          // If human revision is enabled and we have a laraHR quote, use it instead
          let quoteToUse = quote
          let upsellQuoteToUse = laraHrQuote ?? null

          if (isLaraQuote && humanRevisionEnabled && laraHrQuote) {
            quoteToUse = laraHrQuote
            // When using laraHR, save the original lara quote as upsellQuote
            upsellQuoteToUse = {
              ...quote,
              quoteKind: quoteKinds.lara,
            }
          }

          const selectedQuote = {
            ...quoteToUse,
            sourceLanguage,
            targetLanguages,
            subject,
            rate: {
              key: currency,
              value: rates[currency],
            },
            upsellQuote: upsellQuoteToUse,
          }

          storage.storeSelectedQuote(selectedQuote)
          storage.clearSwornOrderPending()
          // Save the first projectId sent to "quote selected" stage
          // This is the key of the deal on HubSpot and must be preserved even if new quotes are created
          storage.storeFirstQuoteProjectId(quote.id)

          const userToken = storage.getCurrentUserToken()
          if (!userToken) {
            storage.hbapi_updateInstantQuote(quoteCode, {
              id: selectedQuote.id,
              price: selectedQuote.price,
              quoteKind: selectedQuote.quoteKind,
            })
          } else {
            const user = decodeJwt(userToken)
            // hubspot.hbapi_chooseProject è il nuovo servizio, quindi lo aspettiamo
            await hubspot.hbapi_chooseProject(
                user.email,
                quoteCode,
                selectedQuote,
            )
          }
          goToProjectDetails()
        }
      },
      [
        sourceLanguage,
        targetLanguages,
        subject,
        rates,
        currency,
        laraHrQuote,
        goToProjectDetails,
      ],
  )

  const quotesArePending = useMemo(
      () =>
          posteditingQuoteIsPending &&
          translationQuoteIsPending &&
          translationRevisionQuoteIsPending &&
          transcreationQuoteIsPending &&
          laraInstantQuoteIsPending,
      [
        posteditingQuoteIsPending,
        translationQuoteIsPending,
        translationRevisionQuoteIsPending,
        transcreationQuoteIsPending,
        laraInstantQuoteIsPending,
      ],
  )

  const isAiQuoteEnabled =
      thirdCardSolution === 'instant' || thirdCardSolution === 'link'

  return (
      <div className={'instant-quote-form__info_ai_enabled_wrapper'}>
        <div className={'instant-quote-form'}>
          <InstantQuoteForm
              sourceLanguage={sourceLanguage}
              targetLanguages={targetLanguages}
              subject={subject}
              deliveryDate={deliveryDate}
              languagesState={languagesState}
              mostPopularTargetLanguages={mostPopularTargetLanguages}
              subjects={subjects}
              isFormSubmitted={isFormSubmitted}
              isDeliveryDateModalVisible={isDeliveryDateModalVisible}
              deliveryMaxSelectableDate={deliveryMaxSelectableDate}
              selectedTimeZone={selectedTimeZone}
              quotesArePending={quotesArePending}
              isFormChanged={isFormChanged}
              handleSetSourceLanguage={handleSetSourceLanguage}
              handleSetTargetLanguages={handleSetTargetLanguages}
              toggleTargetLanguage={toggleTargetLanguage}
              resetSelectedTargetOptions={resetSelectedTargetOptions}
              handleSetSubject={handleSetSubject}
              getDeliveryDateText={getDeliveryDateText}
              showDeliveryDateModal={showDeliveryDateModal}
              onDismissDeliveryDateModal={onDismissDeliveryDateModal}
              setDeliveryDateAndTimezone={setDeliveryDateAndTimezone}
              handleSubmit={handleSubmit}
          />
          <InstantQuoteResult
              showQuotes={showQuotes}
              isFormChanged={isFormChanged}
              quotesArePending={quotesArePending}
              translatorsCount={translatorsCount}
              isAiQuoteEnabled={isAiQuoteEnabled}
              firstCardSolution={firstCardSolution}
              secondCardSolution={secondCardSolution}
              thirdCardSolution={thirdCardSolution}
              posteditingQuote={posteditingQuote}
              translationQuote={translationQuote}
              translationRevisionQuote={translationRevisionQuote}
              transcreationQuote={transcreationQuote}
              laraInstantQuote={laraInstantQuote}
              posteditingQuoteIsPending={posteditingQuoteIsPending}
              translationQuoteIsPending={translationQuoteIsPending}
              translationRevisionQuoteIsPending={translationRevisionQuoteIsPending}
              transcreationQuoteIsPending={transcreationQuoteIsPending}
              laraInstantQuoteIsPending={laraInstantQuoteIsPending}
              targetLanguages={targetLanguages}
              currency={currency}
              rates={rates}
              selectedTimeZone={selectedTimeZone}
              deliveryDate={deliveryDate}
              showDeliveryDateModal={showDeliveryDateModal}
              onSelectQuote={onSelectQuote}
          />
        </div>
        <div className={'instant-quote-form__info_ai_enabled'}>
        <span className={'badge'}>
          {i18n.t('Pages.InstantQuote.infoBadge')}
        </span>{' '}
          <div>
            {i18n.t('Pages.InstantQuote.infoText')}
            <a href={domBridge.getLinkToPayAfterDeliveryInfo()}>
              {i18n.t('Pages.InstantQuote.infoLinkLabel')}
            </a>
          </div>
        </div>
      </div>
  )
}

InstantQuote.propTypes = {
  // store props
  wordCount: PropTypes.number.isRequired,
  files: PropTypes.array.isRequired,
  // store actions
  setWordCount: PropTypes.func.isRequired,
  removeFiles: PropTypes.func.isRequired,
}

export default connect(['wordCount', 'files'], actions)(InstantQuote)
